import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { NeighborhoodLinks } from "@/components/NeighborhoodLinks";
import { serviceAreas } from "@/data/serviceAreas";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import control4Asset from "@/assets/control4-c4yourself.png.asset.json";
const img = control4Asset.url;

const URL = "https://apexav.com/services/control4";
const OG_IMAGE = abs(img);
const TITLE = "Control4 Dealer & Installer in Denver | Apex AV";
const DESCRIPTION =
  "Authorized Control4 dealer in Denver. Certified Control4 installation, programming, and support for lighting, audio, video, shades, climate, and security.";

const faqs = [
  {
    q: "Is Apex AV an authorized Control4 dealer in Denver?",
    a: "Yes. Apex AV is an authorized Control4 dealer serving Denver, Cherry Creek, Highlands Ranch, Castle Rock, Castle Pines, Parker, and the wider Front Range. Control4 is sold only through certified dealers, so every system is designed, installed, and programmed by our team rather than sold as a DIY kit.",
  },
  {
    q: "How much does a Control4 system cost in Denver?",
    a: "A single-room Control4 system with a controller, remote, and a few integrations typically starts in the low thousands. Whole-home Control4 automation covering lighting, shades, multi-room audio, video distribution, climate, and cameras generally runs from the mid five figures upward depending on square footage and how much wiring already exists.",
  },
  {
    q: "What can Control4 control in my home?",
    a: "Control4 unifies lighting and keypads, motorized shades, whole-home audio and video, home theater, thermostats and multi-zone HVAC, smart locks, video doorbells, cameras, irrigation, pool and spa equipment, and voice assistants — all from one app, remote, or touchscreen.",
  },
  {
    q: "Can Control4 be added to an existing home without major remodeling?",
    a: "Yes. Control4 supports wireless lighting, thermostats, locks, and audio, so most finished Denver homes can be automated with minimal drywall work. Where wired reliability matters — theater rooms, in-wall keypads, cameras, and access points — we retrofit cable with small access cuts and clean patching.",
  },
  {
    q: "How does Control4 compare to other smart home platforms?",
    a: "Control4 is the strongest all-rounder for whole-home integration: the widest device driver library, a mature interface, and a professional dealer network for support. Lighting-first platforms can be a better fit when lighting is the entire project, and server-based systems suit very large estates. We spec whichever fits your home and budget.",
  },
];

const bullets = [
  "Authorized Control4 dealer — design, install, and programming by certified technicians",
  "Control4 controllers, touchscreens, keypads, and remotes sized to your home",
  "Lighting and motorized shade control with scene-based keypads in any finish",
  "Whole-home audio and 4K video distribution driven from one Control4 rack",
  "Home theater and media room integration with one-button scenes",
  "Climate, smart locks, video doorbells, and 4K camera integration",
  "OS 3 / OS 4 updates plus 4Sight remote access and remote troubleshooting",
];

export const Route = createFileRoute("/services/control4")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "Control4 dealer Denver, Control4 installer Denver, Control4 installation Denver, Control4 programming Denver, Control4 home automation Denver, authorized Control4 dealer Colorado, Control4 smart home Denver, Control4 repair Denver, Control4 dealer near me, Control4 lighting control Denver, Control4 home theater Denver, Control4 remote Denver, Control4 OS 3 upgrade Denver, Control4 4Sight Denver, Control4 takeover Denver, Control4 Cherry Creek, Control4 Highlands Ranch, Control4 Castle Rock, Control4 Castle Pines",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Control4 touchscreen and lighting keypad in a Denver, Colorado luxury living room" },
      { name: "twitter:image", content: OG_IMAGE },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "control4",
            name: "Control4 Home Automation Installation",
            alternateName: ["Control4 Dealer & Installer, Denver CO"],
            description:
              "Authorized Control4 dealer in Denver providing Control4 design, installation, programming, takeover, and ongoing support for lighting, audio, video, shades, climate, and security.",
            pageTitle: TITLE,
            pageDescription: DESCRIPTION,
            image: img,
            faqs,
            serviceType: "Control4 Home Automation Installation",
            breadcrumbName: "Control4 Home Automation",
            brand: "Control4",
            offerCatalog: bullets,
          })
        ),
      },
    ],
  }),
  component: () => (
    <>
      <ServiceDetail
        topics={["control4", "smart-home", "lighting", "shading", "networking"]}
        eyebrow="Control4 dealer"
        title="Control4, installed and programmed right."
        intro={
          <>
            Apex AV is an authorized <strong>Control4 dealer in Denver</strong> — certified{" "}
            <strong>Control4 installation, programming, and support</strong> that ties lighting, shades,
            audio, video, climate, and security into one interface you'll actually use.
          </>
        }
        image={img}
        imageAlt="Control4 touchscreen and lighting keypad controlling a Denver, Colorado luxury living room"
        bulletsHeading="What Control4 installation with Apex AV includes"
        bullets={bullets}
        features={[
          {
            title: "One interface, every system",
            desc: "Lights, shades, music, TV, HVAC, locks, and cameras on one Control4 app, remote, and touchscreen.",
          },
          {
            title: "Programmed around your day",
            desc: "Wake, work, dinner, movie, and goodnight scenes tuned to how your household actually lives.",
          },
          {
            title: "Dealer-supported for life",
            desc: "We're your Control4 dealer of record — most issues are resolved remotely in minutes via 4Sight.",
          },
        ]}
        faqs={faqs}
        serviceAreas={serviceAreas.map((a) => ({ slug: a.slug, name: a.name }))}
        relatedServices={[
          { to: "/services/smart-home", label: "Smart Home Automation", desc: "Whole-home automation design across every platform we install." },
          { to: "/services/lighting", label: "Smart Lighting", desc: "Architectural lighting scenes and keypads driven by Control4." },
          { to: "/services/shading", label: "Motorized Shading", desc: "Shades that track the Front Range sun automatically." },
          { to: "/services/home-theater", label: "Custom Home Theater", desc: "One-button theater scenes controlled from the same interface." },
          { to: "/services/whole-home-audio", label: "Whole-Home Audio", desc: "Multi-room music zones streamed from your Control4 rack." },
          { to: "/services/networking", label: "Networking & Wi-Fi", desc: "The wired backbone every Control4 system depends on." },
        ]}
      />
      <NeighborhoodLinks
        service="smart-home"
        heading="Control4 installation by Denver neighborhood"
        subheading="Local-first design notes for the communities and HOAs we know inside out."
      />
    </>
  ),
});
